/**
 * Contesto geografico pagina per l'Ambasciatore (ChatWidget).
 * Estrae slug / nome / coordinate dalla route corrente.
 */

import { resolveBorgoCoords } from "../data/borgoPaginaTemplateData";

/** Landmark o attrazione iconica per quick-reply contestuali */
const LANDMARK_BY_SLUG = {
  campli: { label: "🏛️ Storia della Scala Santa", message: "Raccontami la storia della Scala Santa di Campli" },
  castelli: { label: "🏛️ Storia della ceramica di Castelli", message: "Raccontami la storia della ceramica di Castelli" },
  "mosciano-sant-angelo": {
    label: "🏛️ Storia e gemellaggi di Mosciano",
    message: "Raccontami la storia e i gemellaggi di Mosciano Sant'Angelo",
  },
  crognaleto: { label: "🏛️ Sentieri della Laga", message: "Raccontami i sentieri e i luoghi da vedere a Crognaleto" },
  "civitella-del-tronto": {
    label: "🏛️ Storia della Fortezza",
    message: "Raccontami la storia della Fortezza di Civitella del Tronto",
  },
  pietracamela: { label: "🏛️ Gran Sasso da Pietracamela", message: "Raccontami Pietracamela e il Gran Sasso" },
  atri: { label: "🏛️ Cattedrale e Pan Ducale", message: "Raccontami la Cattedrale e il Pan Ducale di Atri" },
  assisi: {
    label: "🏛️ Basilica di San Francesco",
    message: "Raccontami la Basilica di San Francesco ad Assisi",
  },
  calascio: { label: "🏛️ Rocca Calascio", message: "Raccontami la storia di Rocca Calascio" },
};

const COORDS_FALLBACK = {
  campli: { lat: 42.4042, lng: 13.7186 },
  "mosciano-sant-angelo": { lat: 42.7411, lng: 13.8878 },
  crognaleto: { lat: 42.5872, lng: 13.4889 },
  castelli: { lat: 42.4778, lng: 13.7124 },
  assisi: { lat: 43.0707, lng: 12.6197 },
};

function titleFromSlug(slug) {
  if (!slug) return "";
  return String(slug)
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * @param {string} pathname
 * @param {URLSearchParams|Record<string,string>|null} [searchParams]
 * @returns {{ slug: string|null, name: string|null, lat: number|null, lng: number|null, kind: string|null }}
 */
export function resolveChatPageContext(pathname, searchParams) {
  const path = String(pathname || "");
  let slug = null;
  let kind = null;

  const patterns = [
    [/^\/comune\/([^/?#]+)/i, "comune"],
    [/^\/borgo\/([^/?#]+)/i, "borgo"],
    [/^\/frazione\/([^/?#]+)/i, "frazione"],
    [/^\/citta\/([^/?#]+)/i, "citta"],
    [/^\/parco\/([^/?#]+)/i, "parco"],
    [/^\/app\/comune\/([^/?#]+)/i, "comune"],
    [/^\/mobile\/comune\/([^/?#]+)/i, "comune"],
  ];

  for (const [re, k] of patterns) {
    const m = path.match(re);
    if (m?.[1]) {
      slug = decodeURIComponent(m[1]).toLowerCase();
      kind = k;
      break;
    }
  }

  // Alias legacy
  if (!slug) {
    if (/^\/mosciano(\/|$)/i.test(path)) {
      slug = "mosciano-sant-angelo";
      kind = "comune";
    } else if (/^\/campli(\/|$)/i.test(path)) {
      slug = "campli";
      kind = "comune";
    } else if (/^\/assisi(\/|$)/i.test(path)) {
      slug = "assisi";
      kind = "comune";
    }
  }

  const getParam = (key) => {
    if (!searchParams) return null;
    if (typeof searchParams.get === "function") return searchParams.get(key);
    return searchParams[key] ?? null;
  };

  const qSlug = getParam("comune") || getParam("slug") || getParam("borgo");
  if (!slug && qSlug) {
    slug = String(qSlug).toLowerCase().trim();
    kind = kind || "query";
  }

  let lat = parseFloat(getParam("lat") || "");
  let lng = parseFloat(getParam("lng") || getParam("lon") || "");
  if (Number.isNaN(lat)) lat = null;
  if (Number.isNaN(lng)) lng = null;

  if (slug && (lat == null || lng == null)) {
    const coords = resolveBorgoCoords({ slug }) || COORDS_FALLBACK[slug];
    if (coords?.lat != null && coords?.lng != null) {
      lat = coords.lat;
      lng = coords.lng;
    }
  }

  const name = slug ? titleFromSlug(slug) : null;
  return { slug, name, lat, lng, kind };
}

/**
 * Quick replies contestuali (label pillola + testo inviato all'AI).
 * @param {{ slug?: string|null, name?: string|null }} ctx
 * @param {string} [lang]
 */
export function buildContextualSuggestions(ctx, lang = "it") {
  const slug = ctx?.slug || null;
  const name = ctx?.name || (slug ? titleFromSlug(slug) : null);

  if (!name) {
    // Fallback rete (non più le 3 frasi statiche Campli-only)
    const defaults = {
      it: [
        { id: "net-mappa", label: "🗺️ Esplora la mappa dei borghi", message: "Suggeriscimi un borgo da visitare sulla mappa 6amico" },
        { id: "net-prodotto", label: "🛍️ Un prodotto tipico del network", message: "Consigliami un prodotto artigianale tipico della rete 6amico" },
        { id: "net-memoria", label: "📖 Racconta una memoria", message: "Voglio raccontare un ricordo di famiglia legato a un borgo" },
      ],
      en: [
        { id: "net-mappa", label: "🗺️ Explore the villages map", message: "Suggest a village to visit on the 6amico map" },
        { id: "net-prodotto", label: "🛍️ A typical local product", message: "Recommend a handcrafted product from the 6amico network" },
        { id: "net-memoria", label: "📖 Share a memory", message: "I want to share a family memory from a village" },
      ],
    };
    return defaults[lang] || defaults.it;
  }

  const landmark = LANDMARK_BY_SLUG[slug];
  const historyLabel = landmark?.label || `🏛️ Storia e monumenti di ${name}`;
  const historyMsg = landmark?.message || `Raccontami la storia e i monumenti principali di ${name}`;

  return [
    { id: "geo-storia", label: historyLabel, message: historyMsg },
    {
      id: "geo-prodotto",
      label: `🍕 Prodotto tipico di ${name}`,
      message: `Suggeriscimi un prodotto tipico o artigianale di ${name}`,
    },
    {
      id: "geo-eventi",
      label: `📅 Eventi a ${name}`,
      message: `Quali eventi, sagre o appuntamenti ci sono a ${name}?`,
    },
  ];
}

/**
 * Testo precompilato per "Genera Itinerario su Misura".
 */
export function buildItineraryPrefill(ctx) {
  const name = ctx?.name || (ctx?.slug ? titleFromSlug(ctx.slug) : "questo borgo");
  return (
    `Genera un itinerario su misura a ${name}: cosa vedere a piedi o in e-bike, ` +
    `tempi indicativi e tappe imperdibili del borgo.`
  );
}

/**
 * Payload API con contesto geografico (campi opzionali).
 */
export function withChatGeoPayload(base, ctx) {
  const out = { ...base };
  if (ctx?.slug) out.comune_slug = ctx.slug;
  if (ctx?.name) out.comune_name = ctx.name;
  if (ctx?.lat != null && !Number.isNaN(ctx.lat)) out.lat = ctx.lat;
  if (ctx?.lng != null && !Number.isNaN(ctx.lng)) out.lng = ctx.lng;
  return out;
}
